import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import PropertyCardV2 from "@/components/PropertyCardV2";
import HeroSearchBar from "@/components/HeroSearchBar";
import TrustScoreWidget from "@/components/TrustScoreWidget";
import VideoInsights from "@/components/VideoInsights";
import BrandVideo from "@/components/BrandVideo";
import HomeServicesStrip from "@/components/HomeServicesStrip";
import { FeaturedAuctions, FeaturedBrands, FeaturedLand } from "@/components/HomeAuctionsBrands";
import {
    USPStripV2, StatsStripV2, ProcessTimeline, HOME_PROCESS_STEPS,
    BuilderPartnersStrip, TestimonialSection, ChannelPartnerCTA,
} from "@/components/PageBlocks";
import { api } from "@/lib/api";
import { ArrowRight, Calendar, Clock } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_ebg-realty-pro/artifacts/bqs0ziee_home%20page%20hero.png";

const HOME_STATS = [
    { v: "250+",       l: "Verified Projects" },
    { v: "25+",        l: "Cities" },
    { v: "10K+",       l: "Happy Customers" },
    { v: "750+",       l: "Channel Partners" },
    { v: "₹8,500 Cr+", l: "Sales Value" },
    { v: "15+",        l: "Years of Trust" },
];

const DEMO_TRUST = {
    total: 92,
    legal_verification: 20,
    builder_reputation: 14,
    construction_quality: 9,
    delivery_track_record: 9,
    appreciation_potential: 9,
    location_connectivity: 14,
    livability: 9,
    documentation_status: 8,
};

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [insights, setInsights] = useState([]);

    useEffect(() => {
        api.get("/properties", { params: { featured: true, limit: 8 } })
            .then((r) => setFeatured((r.data.items || []).filter((p) => p.listing_type !== "auction" && !["Land", "Plot"].includes(p.type)).slice(0, 4)))
            .catch(() => {});
        api.get("/insights", { params: { limit: 5 } })
            .then((r) => setInsights((r.data.items || []).slice(0, 5)))
            .catch(() => {});
    }, []);

    return (
        <PageShell navVariant="dark">
            {/* ============ HERO + USP together = exactly 100vh (no white gap on any viewport) ============ */}
            <div className="flex flex-col" style={{ minHeight: "100vh" }}>
                <section data-testid="home-hero" className="relative flex-1 bg-[#0a0d18] text-white overflow-hidden flex items-center">
                    <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover"/>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/60 to-transparent"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d18]/80 via-transparent to-[#0a0d18]/40"/>
                    <div className="relative max-w-[1440px] w-full mx-auto px-6 md:px-10 pt-24 pb-8">
                    <div className="text-[10px] md:text-[11px] tracking-[0.42em] font-bold text-[var(--ebg-gold)] uppercase mb-4" data-testid="hero-eyebrow">
                        Buy&nbsp;&nbsp;|&nbsp;&nbsp;Invest&nbsp;&nbsp;|&nbsp;&nbsp;Grow
                    </div>
                    <h1 className="font-serif text-3xl md:text-5xl lg:text-[64px] leading-[1.05] max-w-5xl">
                        India&rsquo;s Most Trusted<br/>
                        Legal-Verified<br/>
                        <em className="not-italic text-[var(--ebg-gold)]">Real Estate Platform</em>
                    </h1>
                    <p className="text-white/75 max-w-2xl mt-5 leading-relaxed text-sm md:text-base">
                        Verified properties. Transparent deals. Legal assurance.<br className="hidden md:block"/>
                        Your trusted partner in every property decision.
                    </p>
                    <div className="mt-7">
                        <HeroSearchBar/>
                    </div>
                </div>
            </section>

            {/* ============ 8 USP STRIP ============ */}
            <USPStripV2 />
            </div>
            {/* end 100vh hero+USP wrapper */}

            {/* ============ SIGNATURE PROJECTS ============ */}
            <section data-testid="signature-projects" className="bg-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Handpicked Excellence</div>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">Our Signature Projects</h2>
                            <p className="text-black/60 mt-4 max-w-xl leading-relaxed">Premium properties. Prime locations. Perfect investments.</p>
                        </div>
                        <Link
                            to="/properties"
                            data-testid="home-view-all-projects"
                            className="hidden md:inline-flex items-center gap-2 border border-black text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-black hover:text-white transition-colors"
                        >
                            View All Projects <ArrowRight size={12}/>
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                        {featured.map((p, i) => <PropertyCardV2 key={p.id} item={p} index={i}/>)}
                    </div>
                    <div className="md:hidden mt-10 text-center">
                        <Link to="/properties" className="inline-flex items-center gap-2 border border-black text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-bold">
                            View All Projects <ArrowRight size={12}/>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============ LAND OPPORTUNITIES (right after Signature Projects) ============ */}
            <FeaturedLand/>

            {/* ============ OUR SERVICES STRIP ============ */}
            <HomeServicesStrip/>

            {/* ============ AUCTIONS + BRANDS ============ */}
            <FeaturedAuctions/>
            <FeaturedBrands/>

            {/* ============ STATS STRIP ============ */}
            <StatsStripV2 items={HOME_STATS}/>

            {/* ============ BRAND VIDEO (Founder story) ============ */}
            <BrandVideo/>

            {/* ============ EBG TRUST SCORE FEATURE ============ */}
            <section data-testid="home-trust-feature" className="bg-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-16">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">The EBG Trust Score</div>
                            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.05]">
                                One score. Eight factors.<br/>
                                <em className="not-italic text-[var(--ebg-gold)]">Total confidence.</em>
                            </h2>
                            <p className="text-black/65 mt-5 leading-relaxed max-w-lg">
                                Our proprietary 0–100 scoring model, verified by our legal, valuation and delivery teams.
                                Every listing carries its own transparent score so you know exactly what you&rsquo;re paying for.
                            </p>
                            <ul className="mt-6 space-y-2.5 text-[14px] text-black/75">
                                {[
                                    "Legal verification & documentation status",
                                    "Builder reputation & delivery track record",
                                    "Construction quality & livability",
                                    "Appreciation potential & location connectivity",
                                ].map((b, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <span className="w-5 h-5 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center shrink-0 mt-0.5 font-bold text-[10px]">✓</span>
                                        {b}
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/legal-check"
                                data-testid="home-trust-cta"
                                className="mt-8 inline-flex items-center gap-2 bg-black text-white px-7 py-3.5 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors"
                            >
                                See How We Score <ArrowRight size={12}/>
                            </Link>
                        </div>
                        <div>
                            <TrustScoreWidget trustScore={DEMO_TRUST}/>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ HOW IT WORKS ============ */}
            <ProcessTimeline
                variant="dark"
                eyebrow="How It Works"
                heading="Buy in five confident steps."
                steps={HOME_PROCESS_STEPS}
            />

            {/* ============ MARKET INSIGHTS ============ */}
            <section data-testid="home-insights" className="bg-[var(--ebg-ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Market Insights</div>
                            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">Sharp reports.<br/>Smarter decisions.</h2>
                        </div>
                        <Link to="/insights" data-testid="home-insights-cta" className="inline-flex items-center gap-2 border border-black text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-black hover:text-white transition-colors">
                            Explore All Insights <ArrowRight size={12}/>
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
                        {insights.map((ins, i) => (
                            <Link
                                key={ins.id}
                                to={`/insights/${ins.slug}`}
                                data-testid={`home-insight-${i}`}
                                className={`group ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}
                            >
                                <div className={`relative overflow-hidden bg-black/5 border border-black/8 ${i === 0 ? "aspect-[4/5]" : "aspect-[4/3]"}`}>
                                    <img src={ins.cover_image || ins.image} alt={ins.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                                    <div className="absolute top-4 left-4 bg-black/80 text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold">{ins.category}</div>
                                </div>
                                <div className="mt-4">
                                    <h3 className={`font-serif leading-tight text-black group-hover:text-[var(--ebg-gold)] transition-colors ${i === 0 ? "text-2xl md:text-3xl" : "text-base md:text-lg"}`}>{ins.title}</h3>
                                    <div className="flex items-center gap-3 text-[11px] text-black/50 mt-2">
                                        <span className="flex items-center gap-1"><Calendar size={11}/> {(() => { const d = ins.published_at || ins.date || ins.created_at; if (!d) return ""; const dt = new Date(d); return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); })()}</span>
                                        <span className="flex items-center gap-1"><Clock size={11}/> {ins.read_minutes || 6} min read</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ BUILDER PARTNERS ============ */}
            <BuilderPartnersStrip/>

            {/* ============ VIDEO INSIGHTS ============ */}
            <VideoInsights/>

            {/* ============ TESTIMONIAL ============ */}
            <TestimonialSection/>

            {/* ============ CHANNEL PARTNER B2B ============ */}
            <ChannelPartnerCTA/>
        </PageShell>
    );
}
